import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VenueType, ZoneType } from '@vigilo/shared';
import { get } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_CROWD_TAXONOMY } from '../data/mock';

export interface CrowdTaxonomyItem {
  code: string;
  label: string;
  shortCode: string | null;
  description: string | null;
  sortOrder: number;
}

export interface CrowdZonePreset {
  zoneType: ZoneType;
  label: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  defaultThresholds: Record<string, number>;
  rules: unknown[];
}

export interface CrowdTaxonomy {
  applicationId: 'crowd_monitoring';
  venueTypes: Array<CrowdTaxonomyItem & { code: VenueType }>;
  zoneTypes: Array<CrowdTaxonomyItem & { code: ZoneType }>;
  operationEventTypes: CrowdTaxonomyItem[];
  zonePresets: CrowdZonePreset[];
}

interface CrowdTaxonomyHookState {
  taxonomy: CrowdTaxonomy | null;
  loading: boolean;
  error: string | null;
  venueTypeLabel: (type: VenueType) => string;
  zoneTypeLabel: (type: ZoneType) => string;
  zoneTypeCode: (type: ZoneType) => string;
  operationEventTypeLabel: (type: string) => string;
  refresh: () => Promise<void>;
}

function unwrap<T>(body: T | { data?: T }): T {
  const b = body as { data?: T };
  return (b.data !== undefined ? b.data : body) as T;
}

function fallbackLabel(value: string, unsupported: string): string {
  return value ? unsupported : 'Unknown';
}

function fallbackOperationEventTypeLabel(value: string): string {
  return value
    ? value
      .split(/[_-]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
    : 'Unknown';
}

export function useCrowdTaxonomy(): CrowdTaxonomyHookState {
  const { isMock } = useMockMode();
  const [taxonomy, setTaxonomy] = useState<CrowdTaxonomy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, 40));
        setTaxonomy(MOCK_CROWD_TAXONOMY);
      } else {
        const body = await get<CrowdTaxonomy | { data?: CrowdTaxonomy }>('/crowd/taxonomy');
        setTaxonomy(unwrap(body));
      }
    } catch {
      setError('Failed to load Crowd taxonomy');
      setTaxonomy(null);
    } finally {
      setLoading(false);
    }
  }, [isMock]);

  useEffect(() => { refresh(); }, [refresh]);

  const venueLabels = useMemo(() => new Map(
    taxonomy?.venueTypes.map(item => [item.code, item.label]) ?? [],
  ), [taxonomy]);
  const zoneLabels = useMemo(() => new Map(
    taxonomy?.zoneTypes.map(item => [item.code, item.label]) ?? [],
  ), [taxonomy]);
  const zoneCodes = useMemo(() => new Map(
    taxonomy?.zoneTypes.map(item => [item.code, item.shortCode ?? item.code.toUpperCase()]) ?? [],
  ), [taxonomy]);
  const operationEventLabels = useMemo(() => new Map(
    taxonomy?.operationEventTypes.map(item => [item.code, item.label]) ?? [],
  ), [taxonomy]);

  return {
    taxonomy,
    loading,
    error,
    refresh,
    venueTypeLabel: (type: VenueType) => venueLabels.get(type) ?? fallbackLabel(type, 'Unsupported Venue Type'),
    zoneTypeLabel: (type: ZoneType) => zoneLabels.get(type) ?? fallbackLabel(type, 'Unsupported Zone Type'),
    zoneTypeCode: (type: ZoneType) => zoneCodes.get(type) ?? 'UNK',
    operationEventTypeLabel: (type: string) => operationEventLabels.get(type) ?? fallbackOperationEventTypeLabel(type),
  };
}
